import React from 'react';

import { useYearQuarterNavigation } from 'hooks';
import { camelCase, lowerCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { Quantity } from '../types';

import { CAPPLAN_TABS } from '../../enums';
import { FunctionColor } from 'components/enums';

import { GroupBy, Quantities } from '../constants';

import { getPeriods } from '../utils';
import { getLocalizedMonthNames } from 'utils/date';
import { displayRoundedNumber } from 'utils/misc';

import { compareWithAtom, groupByAtom } from '../atoms';

import LaunchIcon from '@mui/icons-material/Launch';
import {
  Box,
  ButtonBase,
  Table,
  TableBody,
  TableCell,
  TableHead
} from '@mui/material';

import { TableHeaderRow, ZebraStripedTableRow } from 'components/shared/Table';
import { StyledTableContainer } from 'components/shared/Table/TableContainer.components';

const monthNames = getLocalizedMonthNames();

type Props = {
  quantities: Quantity[];
  showDetailsSubTab: (tab: keyof typeof CAPPLAN_TABS) => void;
};

export default function AnalyzerTable({
  quantities,
  showDetailsSubTab
}: Props) {
  const { t } = useTranslation('common');
  const thPeriods = getPeriods(quantities);

  const groupBy = useRecoilValue(groupByAtom);
  const compareWith = useRecoilValue(compareWithAtom);

  const { isViewPerWeek } = useYearQuarterNavigation();

  return (
    <StyledTableContainer>
      <Table className="analyzer-table">
        <TableHead>
          <TableHeaderRow>
            {[t('description')]
              .concat(
                isViewPerWeek
                  ? thPeriods.map((p) => `${t('week')} ${p.periodNr}`) || []
                  : monthNames
              )
              .concat(t(isViewPerWeek ? 'quarterTotal' : 'yearTotal'))
              .map((val) => (
                <TableCell key={val}>{val}</TableCell>
              ))}
          </TableHeaderRow>
        </TableHead>
        <TableBody>
          {quantities.map((quantity) => (
            <React.Fragment key={`${quantity.name}${quantity.scenarioId}`}>
              <ZebraStripedTableRow>
                <TableCell>
                  {((quantity) => {
                    const { name } = quantity;
                    const nameKey = camelCase(lowerCase(name));
                    const compareWithNls = t(
                      `analyzer.filters.${camelCase(lowerCase(compareWith))}`
                    );
                    const nameNls =
                      compareWith && compareWith !== name
                        ? `${t(`analyzer.filters.${nameKey}`)} ${t(
                            'comparedTo'
                          )} ${compareWithNls}`
                        : t(`analyzer.filters.${nameKey}`);
                    if (
                      name === CAPPLAN_TABS.WORK_DEMAND ||
                      name === CAPPLAN_TABS.FORMATION ||
                      name === CAPPLAN_TABS.BASE_PLAN
                    ) {
                      return (
                        <ButtonBase
                          type="button"
                          title={`${t('gotoPage')} ${nameNls}`}
                          className="clean-btn anchor-btn"
                          style={{
                            textAlign: 'left'
                          }}
                          onClick={() => showDetailsSubTab(name)}
                        >
                          {nameNls}
                          <LaunchIcon
                            color="inherit"
                            sx={{
                              fontSize: '16px'
                            }}
                          />
                        </ButtonBase>
                      );
                    }
                    return nameNls;
                  })(quantity)}
                </TableCell>
                {quantity.totals.map(({ id, total }) => (
                  <TableCell key={id}>{displayRoundedNumber(total)}</TableCell>
                ))}
              </ZebraStripedTableRow>
              {(!!groupBy ||
                (quantity.name === Quantities.ROSTER && !compareWith)) &&
                quantity.data.map(
                  ({
                    category,
                    periods,
                    functionName,
                    functionDescription,
                    functionColor,
                    id
                  }) => {
                    // Temporary solution, endpoints differ with keys for the moment. Should all be functionDescription
                    const fd = functionDescription || functionName;
                    const description =
                      groupBy === GroupBy.CATEGORY
                        ? category
                          ? t(`dhtCategories.${camelCase(lowerCase(category))}`)
                          : undefined
                        : fd;

                    let colorCode = functionColor || FunctionColor.DEFAULT;

                    if (!description) {
                      colorCode = FunctionColor.UNKNOWN;
                    }

                    if (quantity.name === Quantities.WORK_DEMAND) {
                      colorCode = FunctionColor.WORK_DEMAND;
                    }

                    if (groupBy === GroupBy.CATEGORY) {
                      colorCode = FunctionColor[category || 'UNKNOWN'];
                    }

                    return (
                      <ZebraStripedTableRow key={id}>
                        <TableCell className="padd-cell">
                          <Box display="flex" gap={1} alignItems="center">
                            <Box
                              flexShrink={0}
                              className="bullet"
                              style={{
                                background: colorCode
                              }}
                            />
                            {description || t('unknown')}
                          </Box>
                        </TableCell>
                        {periods.map((p, i) => (
                          <TableCell key={`${id}-${p.periodNr}-${i}`}>
                            {displayRoundedNumber(p.hours)}
                          </TableCell>
                        ))}
                        <TableCell>
                          {displayRoundedNumber(
                            periods.reduce((prev, val) => prev + val.hours, 0)
                          )}
                        </TableCell>
                      </ZebraStripedTableRow>
                    );
                  }
                )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
}
